import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from "react-i18next";
import { devicesActions, globalActions, locationActions } from '../_actions';
import { MainPageWithButtons, ContentSpace, SimpleToggle } from '../_components';
import { GLOBAL_POPUPS } from '../_constants';
import { LocationInfo } from '../_models/LocationInfo';
import "leaflet/dist/leaflet.css";
import { appPermissions } from '../_constants/permissions.constants';
import { CapsuleButton } from '../_components/CapsuleButton';
import { locationInfoActions } from '../_actions/locationInfo.actions';
import { TimeStringHelper } from '../_helpers/TimeStringHelper';
import { ReadOnlyDataRow } from '../_components/ReadOnlyDataRow';
import { MapHelper } from './MapHelper';
import { TechnicalServiceModule } from '../TechnicalServiceModule/TechnicalServiceModule';
import { Selectors } from '../_reducers/app.reducer';
import { ValidatedTextInput } from '../../Common/_components/ValidatedTextInput';
import { LocationCoordinatesInput } from '../../Common/_components/LocationCoordinatesInput';
import { VACATION_RENTAL_PLANS } from '../../PMS/_constants/vacationRentalPlan.constants';
import { CustomNumberInput } from '../../Common/_components/CustomNumberInput';
import { InlineEditButtons } from '../_components/InlineEditButtons';
import { MyInvitationsComponent } from './MyInvitationsComponent';
import { ValidationHelper} from '../_helpers';
import { EverouModulesActions } from '../_stores/ModulesStore/EverouModulesActions';
import { BARRIER_MODES } from '../_constants/barriers.constants';
import { WaitSpinner } from "../_components/WaitSpinner";
import { SUBDOMAIN_IDS } from '../../SUBDOMAIN_IDS';

// Rango del geovallado, tomado de las apps: iOS kGPSRegionMinRadius/Default = 200/300
// y Android LOCATION_MIN_ACCURACY = 200 con un selector que llega a 750.
const DETECTION_ZONE_MIN = 200;
const DETECTION_ZONE_MAX = 750;
const DETECTION_ZONE_DEFAULT = 300;

const TEST_IDS = {
    nameSection: "location-config-name-section",
    nameSaveButton: "location-config-name-save-button",
    nameInput: "location-config-name-input",

    companyNameSection: "location-config-company-name-section",
    companyNameSaveButton: "location-config-company-name-save-button",
    companyNameInput: "location-config-company-name-input",

    companyContactEmailSection: "location-config-company-contact-email-section",
    companyContactEmailSaveButton: "location-config-company-contact-email-save-button",
    companyContactEmailInput: "location-config-company-contact-email-input",

    companyLegalIdSection: "location-config-company-legal-id-section",
    companyLegalIdSaveButton: "location-config-company-legal-id-save-button",
    companyLegalIdInput: "location-config-company-legal-id-input",

    companyAdditionalInfoSection: "location-config-company-additional-info-section",
    companyAdditionalInfoSaveButton: "location-config-company-additional-info-save-button",
    companyAdditionalInfoInput: "location-config-company-additional-info-input",
};

SettingsModule.TEST_IDS = TEST_IDS;

export function SettingsModule() {

    //LIBRARIES
    const { t } = useTranslation();
    const dispatch = useDispatch();

    //GLOBAL STATE
    let locationInfo    = LocationInfo();
    locationInfo        = useSelector(Selectors.getLocationInfo);
    const locationUid   = useSelector(Selectors.getSelectedLocationUid);
    const [isBarrierEnabled, setIsBarrierEnabled] = useState(locationInfo.isBarrierEnabled);
    //LOCAL STATE
    const [map, setMap] = useState();
    // Sin valor guardado las apps usan 300 (kGPSRegionDefaultRadius), asi que es
    // lo que se muestra: es el radio que el movil aplica de verdad.
    const [detectionZoneLocal, setDetectionZoneLocal] = useState(
        locationInfo.radius ?? DETECTION_ZONE_DEFAULT,
    );
    // La carga pendiente viaja con el timer (metros Y la localizacion a la que
    // pertenecen) para poder volcarla al salir sin escribir sobre la que este
    // abierta para entonces.
    const detectionZoneSaveTimer = useRef(null);
    const detectionZonePendingSave = useRef(null);

    const permission    = useSelector(Selectors.getPermissionSelectedLocation);
    const entrypointHost = useSelector(Selectors.getEntrypointHost);
    const isClockinsSubdomain = entrypointHost === SUBDOMAIN_IDS.EVEROU_CLOCKINS_PRESET;
    const canViewCompanyData = isClockinsSubdomain && appPermissions.canViewLocationCompanyData(
        permission,
    );
    
    const canToggleBarrierSystem = appPermissions.canManageBarriers(
        permission,
    );

    // Coordenadas y zona de deteccion valen para toda la localizacion, asi que
    // solo las edita quien puede editarla: el resto de filas son de solo lectura.
    const canEditLocationSettings = appPermissions.canEditLocationSettings(
        permission,
    );

    const invitation = useMemo(() => locationInfo?.invitationData, [locationInfo?.invitationData])

    //ACTION HANDLERS
    const handleRemoveCurrentLocation = useCallback(() => {
        dispatch(globalActions.showPopUp(GLOBAL_POPUPS.DELETE_LOCATION, { location: locationInfo }));
    }, [dispatch, locationInfo]);

    const onChangeBarrierMode = useCallback(async enabled => {
        try {
            setIsBarrierEnabled(enabled);
            await dispatch(locationActions.updateBarrierMode(enabled ? BARRIER_MODES.ENABLED : BARRIER_MODES.DISABLED, locationUid));
            await dispatch(devicesActions.getDevicesFromLocation(locationUid));
            dispatch(EverouModulesActions.buildModules());
        } catch {
            setIsBarrierEnabled(!enabled);
        }
    }, [dispatch, locationUid]);

    useEffect(() => {
        setDetectionZoneLocal(locationInfo.radius ?? DETECTION_ZONE_DEFAULT);
    }, [locationInfo.radius]);

    // La escritura va con debounce, y salir de la pantalla dentro de esos 600 ms
    // es de lo mas normal: se vuelca en vez de descartarse, o el cambio hecho
    // justo antes de salir se pierde en silencio. La localizacion va en la carga
    // pendiente, asi que no se escribe sobre la que se este viendo despues.
    useEffect(() => () => {
        if (detectionZoneSaveTimer.current)
            clearTimeout(detectionZoneSaveTimer.current);

        const pending = detectionZonePendingSave.current;
        if (!pending)
            return;

        dispatch(locationActions.updateDetectionZone(pending.metres, pending.uid));
    }, [dispatch]);

    useEffect(() => {
        setIsBarrierEnabled(locationInfo.isBarrierEnabled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[locationUid])

    //INITIALIZATION
    // The coordinates are editable from this same screen, so the map follows
    // them instead of only the selected location. The invitation fetch stays in
    // its own effect: it has nothing to do with the map and re-running it on
    // every coordinate save was an extra request per edit.
    useEffect(() => {
        if (map)
            map.remove();

        const newMap = MapHelper.createMap({
            id: "map",
            latitude: locationInfo.latitude,
            longitude: locationInfo.longitude,
            radius: locationInfo.radius,
        });

        setMap(newMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationUid, locationInfo.latitude, locationInfo.longitude, locationInfo.radius]);

    useEffect(() => {
        if (invitation)
            return;

        dispatch(locationInfoActions.getCreationInvitation(
            locationInfo.invitationUid,
        ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, locationUid]);


    //VIEW
    return (
        <ContentSpace
            mainContent={mainContent()}
        />
    );
    
    function mainContent() {
        return (
            <MainPageWithButtons
                pageTitle={t("settings.locationSettings")}
                content={content()}
                buttons={[
                    <CapsuleButton
                        testId={"cancel-invitation-button"}
                        text={appPermissions.canUserDeleteLocation(locationInfo.locationPermission)
                            ? t("settings.deleteLocation")
                            : t("settings.cancelInvitation")
                        }
                        onClick={handleRemoveCurrentLocation}
                        style={`DANGER`}
                    />
                ]}
            />
        );
    }

    function content() {
        const yes = t("global_yes");
        const no = t("global_no");
        return (
            <div
                className="
                h-100
                d-flex
                no-gutters
                flex-column
            "
            >
                {invitation && invitationInfo()}
                <NameInput
                    {...{
                        key: locationInfo.locationName,
                        initialValue: locationInfo.locationName,
                        locationUid,
                    }}
                />
                {dataRow(t("common.timezone"), locationInfo.timezone)}
                {/* Solo Premium: fijar el punto desde el escritorio es una
                    herramienta del PMS. El resto de localizaciones siguen
                    poniendolo desde el movil, con el GPS. */}
                {locationInfo.vacationRentalPlan === VACATION_RENTAL_PLANS.PREMIUM_PLAN &&
                    canEditLocationSettings &&
                    <LocationCoordinatesInput
                        latitude={locationInfo.latitude}
                        longitude={locationInfo.longitude}
                        address={locationInfo.address}
                        onSave={(latitude, longitude, address) => dispatch(
                            locationActions.updateCoordinates(latitude, longitude, address, locationUid),
                        )}
                    />
                }
                {dataRow(
                    t("settings.notificationsEnabled"),
                    locationInfo.isNotificationActive ? yes : no,
                )}
                {dataRow(
                    t("settings.shareData"),
                    locationInfo.shareData ? yes : no,
                )}

                {detectionZoneRow()}

                {dataRow("Id", locationInfo.locationId)}
                {canToggleBarrierSystem && (dataRow(
                    t("mod_barriers_title"),
                    <SimpleToggle
                        handleChange={({ detail: { value } }) =>
                            onChangeBarrierMode(value)
                        }
                        checked={isBarrierEnabled}
                    />,
                ))}

                {canViewCompanyData && (
                    <>
                        <div className="mt-3" />

                        <div
                            className="
                        c-new-ft-m
                        text-uppercase
                        font-weight-bold
                        pt-2
                    "
                        >
                            {t("mod_clockins_company_settings")}
                        </div>

                        <CompanyNameInput
                            {...{
                                key: locationUid + "_company_name",
                                initialValue: locationInfo.companyName,
                                locationUid,
                            }}
                        />
                        <CompanyContactEmailInput
                            {...{
                                key: locationUid + "_company_contact_email",
                                initialValue: locationInfo.companyContactEmail,
                                locationUid,
                            }}
                        />
                        <CompanyLegalIdInput
                            {...{
                                key: locationUid + "_company_legal_id",
                                initialValue: locationInfo.companyCIF,
                                locationUid,
                            }}
                        />
                        <CompanyAdditionalInfoInput
                            {...{
                                key: locationUid + "_company_additional_info",
                                initialValue:
                                    locationInfo.companyAdditionalInfo,
                                locationUid,
                            }}
                        />

                        <div
                            className="
                        pt-2
                    "
                        >
                            {t("mod_clockin_user_disclaimer")}
                        </div>
                    </>
                )}

                <div className="mt-3" />

                <MyInvitationsComponent key={locationUid} />

                <TechnicalServiceModule />

                {/* != null, not truthy: latitude 0 is a real place on the
                    equator, and hiding the container while MapHelper still
                    builds the map leaves Leaflet with no element to attach to. */}
                {locationInfo.latitude != null && locationInfo.longitude != null && (
                    <>
                        <div className="mt-3" />
                        <div className="position-relative w-100 c-ratio-3-2 flex-shrink-0">
                            <div
                                id="map"
                                className="position-absolute w-100 h-100 c-position-overlay border"
                            />
                        </div>
                    </>
                )}
            </div>
        );
    }

    function invitationInfo() {

        return (
            <div data-testid="invitation-info">
                {sourceUserEmail()}
                {periodString()}
            </div>
        );

        ////
        function sourceUserEmail() {
            return dataRow(
                t("invitations.invitedBy"),
                invitation.userSrcEmail,
            );
        }

        function periodString() {
            return dataRow(
                t("common.period"),
                data(),
            );

            function data() {
                return TimeStringHelper.buildInvitationTimeString(
                    invitation,
                    locationInfo.timezone,
                );
            }
        }
    }

    // El geovallado con el que iOS y Android detectan entrada y salida. El rango
    // no es arbitrario: por debajo de 200 m las dos apps lo suben a 200 en
    // silencio (kGPSRegionMinRadius en iOS, LOCATION_MIN_ACCURACY en Android), y
    // el selector de Android no pasa de 750, asi que fuera de [200, 750] se
    // guardaria un numero que el movil no aplica tal cual.
    function detectionZoneRow() {
        function clampMetres(metres) {
            if (!metres || isNaN(metres))
                return DETECTION_ZONE_MIN;

            return Math.min(
                Math.max(Math.round(metres), DETECTION_ZONE_MIN),
                DETECTION_ZONE_MAX,
            );
        }

        function saveMetres(metres) {
            if (detectionZoneSaveTimer.current) {
                clearTimeout(detectionZoneSaveTimer.current);
                detectionZoneSaveTimer.current = null;
                detectionZonePendingSave.current = null;
            }

            if (metres === locationInfo.radius)
                return;

            detectionZonePendingSave.current = { metres, uid: locationUid };
            detectionZoneSaveTimer.current = setTimeout(() => {
                detectionZoneSaveTimer.current = null;
                detectionZonePendingSave.current = null;
                dispatch(locationActions.updateDetectionZone(metres, locationUid));
            }, 600);
        }

        // El radio es un ajuste de toda la localizacion, no de quien lo mira:
        // quien no puede editarla lo ve, como antes, pero sin poder tocarlo.
        if (!canEditLocationSettings)
            return dataRow(t("settings.detectionZone"), `${detectionZoneLocal} m`);

        return (
            <ReadOnlyDataRow
                name={t("settings.detectionZone")}
                data={
                    <div className="d-flex align-items-center">
                        <CustomNumberInput
                            value={detectionZoneLocal}
                            min={1}
                            max={DETECTION_ZONE_MAX}
                            onChange={metres => {
                                setDetectionZoneLocal(metres);
                                saveMetres(clampMetres(metres));
                            }}
                            onBlur={metres => {
                                const clamped = clampMetres(metres);
                                setDetectionZoneLocal(clamped);
                                saveMetres(clamped);
                            }}
                        />
                        <span className="ml-2">m</span>
                    </div>
                }
            />
        );
    }

    function dataRow(name, data) {
        return <ReadOnlyDataRow
            name={name}
            data={data}
        />;
    }
}

function NameInput({ initialValue, locationUid }) {

    //LIBRARIES
    const { t } = useTranslation();
    const dispatch = useDispatch();

    //ACTION HADLERS
    function handleSave(text) {
        dispatch(locationActions.updateLocationName(text, locationUid));
    }

    return (
        <DataWrapper hasSeparator={false}>
            <div
                data-testid={TEST_IDS.nameSection}
                className="d-flex align-items-center justify-content-between"
            >
                <div>
                    {t("global_name")}
                </div>
                <InlineValidatedTextInput
                    inputTestID={TEST_IDS.nameInput}
                    saveButtonTestID={TEST_IDS.nameSaveButton}
                    handleSave={handleSave}
                    initialValue={initialValue}
                    required={true}
                />
            </div>
        </DataWrapper>
    );
}

function CompanyNameInput({ initialValue, locationUid }) {

    //LIBRARIES
    const { t } = useTranslation();
    const dispatch = useDispatch();

    //ACTION HADLERS
    function handleSave(text) {
        dispatch(locationActions.updateCompanyName(text, locationUid));
    }

    return (
        <DataWrapper>
            <div
                data-testid={TEST_IDS.companyNameSection}
                className="d-flex align-items-center justify-content-between"
            >
                <div>
                    {t("mod_clockins_company_name")}
                </div>
                <InlineValidatedTextInput
                    inputTestID={TEST_IDS.companyNameInput}
                    saveButtonTestID={TEST_IDS.companyNameSaveButton}
                    handleSave={handleSave}
                    initialValue={initialValue}
                    required={false}
                />
            </div>
        </DataWrapper>
    );
}

function CompanyContactEmailInput({ initialValue, locationUid }) {

    //LIBRARIES
    const { t } = useTranslation();
    const dispatch = useDispatch();

    //ACTION HADLERS
    function handleSave(text) {
        dispatch(locationActions.updateCompanyContactEmail(text, locationUid));
    }

    return (
        <DataWrapper>
            <div
                data-testid={TEST_IDS.companyContactEmailSection}
                className="d-flex align-items-center justify-content-between"
            >
                <div>
                    {t("mod_clockins_company_contact_email")}
                </div>
                <InlineValidatedMailInput
                    inputTestID={TEST_IDS.CompanyContactEmailInput}
                    saveButtonTestID={TEST_IDS.companyContactEmailSaveButton}
                    handleSave={handleSave}
                    initialValue={initialValue}
                    required={false}
                />
            </div>
        </DataWrapper>
    );
}

function CompanyLegalIdInput({ initialValue, locationUid }) {

    //LIBRARIES
    const { t } = useTranslation();
    const dispatch = useDispatch();

    //ACTION HADLERS
    function handleSave(text) {
        dispatch(locationActions.updateCompanyLegalId(text, locationUid));
    }

    return (
        <DataWrapper>
            <div
                data-testid={TEST_IDS.companyLegalIdSection}
                className="d-flex align-items-center justify-content-between"
            >
                <div>
                    {t("mod_clockins_company_legal_id")}
                </div>
                <InlineValidatedTextInput
                    inputTestID={TEST_IDS.companyLegalIdInput}
                    saveButtonTestID={TEST_IDS.companyLegalIdSaveButton}
                    handleSave={handleSave}
                    initialValue={initialValue}
                    required={false}
                />
            </div>
        </DataWrapper>
    );
}

function CompanyAdditionalInfoInput({ initialValue, locationUid }) {

    //LIBRARIES
    const { t } = useTranslation();
    const dispatch = useDispatch();

    //ACTION HADLERS
    function handleSave(text) {
        dispatch(locationActions.updateCompanyAdditionalInfo(text, locationUid));
    }

    return (
        <DataWrapper>
            <div
                data-testid={TEST_IDS.companyAdditionalInfoSection}
                className="d-flex align-items-center justify-content-between"
            >
                <div>
                    {t("mod_clockins_company_additional_info")}
                </div>
                <InlineValidatedTextInput
                    inputTestID={TEST_IDS.companyAdditionalInfoInput}
                    saveButtonTestID={TEST_IDS.companyAdditionalInfoSaveButton}
                    handleSave={handleSave}
                    initialValue={initialValue}
                    required={false}
                />
            </div>
        </DataWrapper>
    );
}

function InlineValidatedTextInput({
    inputTestID,
    saveButtonTestID,

    handleSave,
    initialValue,

    required,
} = {}) {

    //LOCAL STATE
    const [text, setText] = useState(initialValue);
    const [isValid, setIsValid] = useState(false);
    const [isEdited, setIsEdited] = useState(false);
    
    //OVERRIDING

    useEffect(() => {
        setIsEdited(text !== initialValue);
    }, [text, initialValue]);

    //ACTION HADLERS
    function handleDiscardChanges() {
        setText(initialValue);
    }

    function handleSaveChanges() {
        handleSave(text);
    }

    return (
        <div className="d-flex align-items-center">
            {isEdited &&
                <>
                    <InlineEditButtons
                        acceptButtonTestID={saveButtonTestID}
                        isEnabled={isValid}
                        handleDiscardChanges={handleDiscardChanges}
                        handleConfirmChanges={handleSaveChanges}
                    />
                    <div className="ml-2"/>
                </>
            }
            <ValidatedTextInput
                isRequired={required}
                isEditable

                testID={inputTestID}
                value={text}
                setValue={setText}
                setIsValid={setIsValid}
            />
        </div>
    );
}

function InlineValidatedMailInput({
    inputTestID,
    saveButtonTestID,

    handleSave,
    initialValue,

    required,
} = {}) {

    //LOCAL STATE
    const [text, setText] = useState(initialValue);
    const [isValid, setIsValid] = useState(false);
    const [isEdited, setIsEdited] = useState(false);
    
    //OVERRIDING

    useEffect(() => {
        setIsEdited(text !== initialValue);
    }, [text, initialValue]);

    //ACTION HADLERS
    function handleDiscardChanges() {
        setText(initialValue);
    }

    function handleSaveChanges() {
        handleSave(text);
    }

    function checkTextIsValidMail() {
        if  (!text || ValidationHelper.ValidationFns.isEmailValid(text)) setIsValid(true);
        else setIsValid(false);
    }

    return (
        <div className="d-flex align-items-center">
            {isEdited &&
                <>
                    <InlineEditButtons
                        acceptButtonTestID={saveButtonTestID}
                        isEnabled={isValid}
                        handleDiscardChanges={handleDiscardChanges}
                        handleConfirmChanges={handleSaveChanges}
                    />
                    <div className="ml-2"/>
                </>
            }
            <ValidatedTextInput
                isRequired={required}
                isEditable

                testID={inputTestID}
                value={text}
                setValue={setText}
                setIsValid={checkTextIsValidMail}
            />
        </div>
    );
}

export function DataWrapper({
    children,
    hasSeparator = true,
    key,
}) {
    return (
        <div key={key}>
            <div className="p-1">
                {children}
            </div>
            {hasSeparator && <hr className="m-0"/>}
        </div>
    );
}